import React, { useState, useRef } from 'react';
import { X, Printer, CheckCircle2, ShoppingBag } from 'lucide-react';
import { format } from 'date-fns';
import api from '../../utils/api';

const BillPrintModal = ({ isOpen, onClose, billData }) => {
  const [printFormat, setPrintFormat] = useState('a4'); // 'thermal' | 'a4'
  const printContainerRef = useRef(null);

  if (!isOpen || !billData) return null;

  const { bill, company, branch } = billData;
  const currencySymbol = company?.currencySymbol || '₹';

  const getLogoUrl = () => {
    if (!company?.logo) return null;
    const rawLogo = company.logo;
    if (rawLogo.startsWith('http://') || rawLogo.startsWith('https://') || rawLogo.startsWith('data:')) {
      return rawLogo;
    }
    const cleanPath = rawLogo.startsWith('/') ? rawLogo : `/${rawLogo}`;
    const baseURL = api.defaults?.baseURL || (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 'http://localhost:5000' : 'https://repair-Pybackend.smartgonext.com');
    return `${baseURL}${cleanPath}`;
  };

  const getAccessoryTermsList = () => {
    const raw = company?.accessoryTerms || company?.terms;
    if (!raw) {
      return [
        'All products sold carry standard manufacturer warranty where applicable.',
        'Physical damage or water damage is strictly excluded from warranty.',
        'Goods once sold cannot be returned after 7 days.'
      ];
    }
    if (Array.isArray(raw)) return raw;
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    } catch (e) {
      if (typeof raw === 'string' && raw.trim()) {
        return raw.split('\n').map(t => t.trim()).filter(Boolean);
      }
    }
    return [
      'All products sold carry standard manufacturer warranty where applicable.',
      'Physical damage or water damage is strictly excluded from warranty.',
      'Goods once sold cannot be returned after 7 days.'
    ];
  };

  const handlePrint = () => {
    const printContent = printContainerRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank', 'width=850,height=950');
    if (!printWindow) {
      alert('Please allow popups to print invoice');
      return;
    }

    const isThermal = printFormat === 'thermal';

    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map(node => node.outerHTML)
      .join('\n');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Bill_${bill.billNumber}</title>
          <script src="https://cdn.tailwindcss.com"></script>
          ${styles}
          <style>
            @page {
              size: ${isThermal ? '80mm auto' : 'A4 portrait'};
              margin: ${isThermal ? '2mm' : '10mm'};
            }
            body {
              font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
              color: #0f172a;
              margin: 0;
              padding: ${isThermal ? '2px' : '15px'};
              background-color: #ffffff !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            /* Explicit layout guarantees for browser print engines */
            .grid { display: grid !important; }
            .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
            .flex { display: flex !important; }
            .justify-between { justify-content: space-between !important; }
            .items-start { align-items: flex-start !important; }
            .items-end { align-items: flex-end !important; }
            .items-center { align-items: center !important; }
            .w-80 { width: 20rem !important; }
            .w-1\\/2 { width: 50% !important; }
            .rounded-2xl { border-radius: 1rem !important; }
            .border-2 { border-width: 2px !important; }
            .bg-slate-50 { background-color: #f8fafc !important; }
          </style>
        </head>
        <body>
          <div style="max-width: 800px; margin: 0 auto;">
            ${printContent.innerHTML}
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() { window.close(); }, 500);
              }, 400);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Modal Header */}
        <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-emerald-500/20 border border-emerald-500/30 rounded-xl flex items-center justify-center text-emerald-400">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Bill Generated Successfully</h2>
              <p className="text-xs text-slate-400">Bill No: {bill.billNumber}</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {/* Format Switcher */}
            <div className="bg-slate-800 p-1 rounded-xl flex items-center text-xs font-semibold">
              <button
                onClick={() => setPrintFormat('thermal')}
                className={`px-3 py-1.5 rounded-lg transition-all ${
                  printFormat === 'thermal'
                    ? 'bg-emerald-500 text-white shadow'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Thermal (80mm)
              </button>
              <button
                onClick={() => setPrintFormat('a4')}
                className={`px-3 py-1.5 rounded-lg transition-all ${
                  printFormat === 'a4'
                    ? 'bg-emerald-500 text-white shadow'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                A4 Sheet
              </button>
            </div>

            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Printable Content Preview */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-100 flex justify-center">
          <div
            className={`bg-white shadow-lg border border-gray-200 transition-all ${
              printFormat === 'thermal' ? 'w-[320px] p-4 text-xs font-mono' : 'w-[750px] p-8 text-sm font-sans'
            }`}
          >
            <div ref={printContainerRef}>
              {printFormat === 'thermal' ? (
                /* Thermal Receipt Layout (80mm) */
                <div className="thermal-receipt text-black">
                  <div className="text-center pb-2 border-b">
                    <h2 className="text-base font-bold uppercase tracking-wider">{company?.companyName || 'DIGITAL REPAIR SHOP'}</h2>
                    {branch?.name && <p className="font-semibold">{branch.name}</p>}
                    {branch?.address && <p>{branch.address}</p>}
                    {branch?.phone && <p>Ph: {branch.phone}</p>}
                    {company?.gstNumber && <p className="font-bold">GSTIN: {company.gstNumber}</p>}
                  </div>

                  <div className="py-2 border-b text-[11px]">
                    <div className="flex justify-between">
                      <span>BILL NO: <strong>{bill.billNumber}</strong></span>
                      <span>MODE: <strong>{bill.paymentMode}</strong></span>
                    </div>
                    <div className="flex justify-between mt-0.5">
                      <span>DATE: {bill.billDate ? format(new Date(bill.billDate), 'dd/MM/yyyy hh:mm a') : ''}</span>
                    </div>
                  </div>

                  <div className="py-2 border-b text-[11px]">
                    <p className="font-bold uppercase">CUSTOMER DETAILS:</p>
                    <p>Name: {bill.customerName}</p>
                    <p>Phone: {bill.customerPhone}</p>
                    {bill.customerAddress && <p>Add: {bill.customerAddress}</p>}
                    {bill.customerGst && <p>GST: {bill.customerGst}</p>}
                  </div>

                  <div className="py-2">
                    <table className="w-full thermal-table text-left">
                      <thead>
                        <tr className="border-b">
                          <th className="py-1">ITEM</th>
                          <th className="py-1 text-center">QTY</th>
                          <th className="py-1 text-right">RATE</th>
                          <th className="py-1 text-right">AMT</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bill.items?.map((item, idx) => (
                          <tr key={idx} className="border-b border-gray-200">
                            <td className="py-1 leading-tight font-semibold">
                              {item.productName}
                              {item.partNumber && <div className="text-[9px] text-gray-600">P/N: {item.partNumber}</div>}
                            </td>
                            <td className="py-1 text-center font-bold">{item.quantity}</td>
                            <td className="py-1 text-right">{item.rate.toFixed(2)}</td>
                            <td className="py-1 text-right font-bold">{item.amount.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="py-2 border-t text-[11px] space-y-1">
                    <div className="flex justify-between">
                      <span>SUBTOTAL:</span>
                      <span>{currencySymbol}{bill.subtotal?.toFixed(2)}</span>
                    </div>

                    {bill.discountAmount > 0 && (
                      <div className="flex justify-between text-red-600">
                        <span>DISCOUNT:</span>
                        <span>-{currencySymbol}{bill.discountAmount?.toFixed(2)}</span>
                      </div>
                    )}

                    {bill.gstAmount > 0 && (
                      <div className="flex justify-between">
                        <span>TAX (GST):</span>
                        <span>+{currencySymbol}{bill.gstAmount?.toFixed(2)}</span>
                      </div>
                    )}

                    <div className="flex justify-between text-sm font-black border-t border-b py-1 mt-1">
                      <span>TOTAL PAYABLE:</span>
                      <span>{currencySymbol}{bill.totalAmount?.toFixed(2)}</span>
                    </div>

                    <div className="flex justify-between pt-0.5 font-bold">
                      <span>PAID ({bill.paymentMode}):</span>
                      <span>{currencySymbol}{bill.paidAmount?.toFixed(2)}</span>
                    </div>
                  </div>

                  {branch?.upiId && (
                    <div className="text-center py-2 border-t text-[10px]">
                      <p className="font-bold">UPI Payment ID: {branch.upiId}</p>
                    </div>
                  )}

                  <div className="text-center pt-3 border-t text-[10px]">
                    <p className="font-bold">*** THANK YOU FOR YOUR BUSINESS ***</p>
                    <p className="mt-1 text-gray-500">Goods once sold will be covered under standard warranty.</p>
                  </div>
                </div>
              ) : (
                /* A4 Sheet Invoice Layout */
                <div className="a4-invoice text-slate-800">
                  {/* Top Bar above boxes */}
                  <div className="a4-header-top flex justify-between items-center text-xs font-bold uppercase tracking-wider mb-2 text-slate-800">
                    <span>RETAIL INVOICE</span>
                    <span>{bill.billNumber}</span>
                  </div>

                  {/* Header Box Format (Matching Reference Design) */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {/* Left Box: Bill & Customer Info */}
                    <div className="border-2 border-slate-700 rounded-2xl p-4 text-center flex flex-col justify-center bg-white shadow-sm">
                      <div className="text-xs font-extrabold text-slate-900 uppercase tracking-wide mb-3">
                        INVOICE {bill.billNumber}
                      </div>
                      
                      <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-1">
                        DATE
                      </div>
                      <div className="text-sm font-extrabold text-slate-900 uppercase mb-2">
                        {bill.billDate ? format(new Date(bill.billDate), 'dd/MM/yyyy') : format(new Date(), 'dd/MM/yyyy')}
                      </div>

                      <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                        NAME
                      </div>
                      <div className="text-sm font-extrabold text-slate-900 uppercase mb-2">
                        {bill.customerName}
                      </div>

                      <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                        PHONE
                      </div>
                      <div className="text-sm font-extrabold text-slate-900">
                        {bill.customerPhone}
                      </div>
                    </div>

                    {/* Right Box: Shop Details */}
                    <div className="border-2 border-slate-700 rounded-2xl p-4 text-center flex flex-col justify-between bg-white shadow-sm">
                      <div>
                        {getLogoUrl() ? (
                          <div className="w-16 h-16 rounded-xl border border-slate-200 overflow-hidden mx-auto mb-2 flex items-center justify-center bg-slate-50 relative">
                            <img 
                              src={getLogoUrl()} 
                              alt="Shop Logo" 
                              className="max-w-full max-h-full object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                if (e.target.nextElementSibling) {
                                  e.target.nextElementSibling.style.display = 'flex';
                                }
                              }}
                            />
                            <div 
                              className="w-full h-full bg-slate-100 items-center justify-center font-black text-slate-500 text-lg rounded-xl hidden"
                              style={{ display: 'none' }}
                            >
                              {(company?.companyName || branch?.name || 'EC').substring(0, 2).toUpperCase()}
                            </div>
                          </div>
                        ) : (
                          <div className="w-14 h-14 rounded-xl border border-slate-300 bg-slate-100 flex items-center justify-center mx-auto mb-2 font-black text-slate-400 text-lg">
                            {(company?.companyName || branch?.name || 'EC').substring(0, 2).toUpperCase()}
                          </div>
                        )}

                        <h1 className="text-base font-black text-slate-900 uppercase tracking-tight mb-0.5">
                          {company?.companyName || branch?.name || 'E CARE REPAIRS'}
                        </h1>
                        <p className="text-xs font-semibold text-slate-700">
                          Phone: {company?.phone || branch?.phone || ''}
                        </p>
                      </div>

                      <hr className="border-t border-slate-300 my-2" />

                      <p className="text-xs text-slate-600">
                        {company?.address || branch?.address || ''}
                      </p>
                    </div>
                  </div>

                  {/* Items Table */}
                  <table className="w-full a4-table mb-6 text-left">
                    <thead>
                      <tr>
                        <th className="w-12 text-center">S.N</th>
                        <th>Product Description</th>
                        <th className="w-20 text-center">HSN</th>
                        <th className="w-16 text-center">Qty</th>
                        <th className="w-24 text-right">Rate ({currencySymbol})</th>
                        <th className="w-20 text-right">GST %</th>
                        <th className="w-28 text-right">Amount ({currencySymbol})</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bill.items?.map((item, idx) => (
                        <tr key={idx}>
                          <td className="text-center font-bold text-xs">{idx + 1}</td>
                          <td>
                            <p className="font-bold text-slate-900">{item.productName}</p>
                            {item.partNumber && <p className="text-[10px] text-slate-500">Part No: {item.partNumber}</p>}
                          </td>
                          <td className="text-center text-xs text-slate-600">{item.hsnCode || '-'}</td>
                          <td className="text-center font-bold text-xs">{item.quantity} {item.unit}</td>
                          <td className="text-right text-xs">{item.rate.toFixed(2)}</td>
                          <td className="text-right text-xs">{item.gstRate || 0}%</td>
                          <td className="text-right font-bold text-slate-900 text-xs">{item.amount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Totals Section */}
                  <div className="flex justify-between items-start mb-8">
                    <div className="w-1/2 pr-6">
                      {bill.notes && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs mb-3">
                          <p className="font-bold text-amber-800 mb-0.5">Note / Remarks:</p>
                          <p className="text-amber-900">{bill.notes}</p>
                        </div>
                      )}
                      <div className="text-xs text-slate-600 space-y-1">
                        <p className="font-bold text-slate-800 uppercase tracking-wider mb-1">Terms & Conditions:</p>
                        {getAccessoryTermsList().map((term, idx) => (
                          <p key={idx}>{term.match(/^\d+[\.\)]/) ? term : `${idx + 1}. ${term}`}</p>
                        ))}
                      </div>
                    </div>

                    <div className="w-80 bg-slate-50/90 border border-slate-200 rounded-2xl p-4 space-y-2 text-xs">
                      <div className="flex justify-between text-slate-600">
                        <span>Subtotal:</span>
                        <span className="font-bold">{currencySymbol}{bill.subtotal?.toFixed(2)}</span>
                      </div>

                      {bill.discountAmount > 0 && (
                        <div className="flex justify-between text-red-600 font-semibold">
                          <span>Discount:</span>
                          <span>-{currencySymbol}{bill.discountAmount?.toFixed(2)}</span>
                        </div>
                      )}

                      {bill.gstAmount > 0 && (
                        <div className="flex justify-between text-slate-600">
                          <span>GST Tax Amount:</span>
                          <span>+{currencySymbol}{bill.gstAmount?.toFixed(2)}</span>
                        </div>
                      )}

                      <div className="flex justify-between text-base font-black text-slate-900 border-t border-b border-slate-200 py-2 my-1">
                        <span>Grand Total:</span>
                        <span>{currencySymbol}{bill.totalAmount?.toFixed(2)}</span>
                      </div>

                      <div className="flex justify-between text-slate-700 font-bold pt-1">
                        <span>Paid Amount ({bill.paymentMode}):</span>
                        <span className="text-emerald-600 font-extrabold">{currencySymbol}{bill.paidAmount?.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Signature Section */}
                  <div className="border-t border-slate-200 pt-10 mt-10">
                    <div className="flex justify-between items-end">
                      <div className="text-left text-xs text-slate-500 font-medium">
                        <p>Customer Signature</p>
                      </div>
                      <div className="text-right text-xs text-slate-800">
                        <p className="font-extrabold text-slate-900 mb-8">{company?.companyName || branch?.name || 'DIGITAL REPAIR SHOP'}</p>
                        <p className="text-slate-500 font-medium">Authorized Signatory</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal Actions */}
        <div className="bg-slate-900 border-t border-slate-800 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 text-xs text-slate-400">
            <ShoppingBag className="w-4 h-4 text-emerald-400" />
            <span>Bill items stock auto-deducted in inventory</span>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl font-semibold text-xs transition-colors"
            >
              Done / Close
            </button>
            
            <button
              onClick={handlePrint}
              className="px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white rounded-xl font-extrabold text-xs transition-all shadow-lg shadow-emerald-950/40 flex items-center space-x-2"
            >
              <Printer className="w-4 h-4" />
              <span>Print Bill ({printFormat === 'thermal' ? 'Thermal 80mm' : 'A4 Sheet'})</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default BillPrintModal;
